import { useState, useCallback } from 'preact/hooks';
import { useStore } from '../lib/store';
import { addresses as addrApi, proofs as proofsApi } from '../lib/api';

interface AliasesProps {
  domain: string;
}

const PRICING: { chars: string; sats: string }[] = [
  { chars: '1 char', sats: '1,000,000 sats' },
  { chars: '2 chars', sats: '100,000 sats' },
  { chars: '3 chars', sats: '50,000 sats' },
  { chars: '4 chars', sats: '10,000 sats' },
  { chars: '5 chars', sats: '5,000 sats' },
  { chars: '6+ chars', sats: '1,000 sats' },
];

function priceForLength(len: number): string {
  if (len === 1) return '1,000,000';
  if (len === 2) return '100,000';
  if (len === 3) return '50,000';
  if (len === 4) return '10,000';
  if (len === 5) return '5,000';
  return '1,000';
}

export function Aliases({ domain }: AliasesProps) {
  const { addresses, setAddresses, proofs, setProofs, showToast } = useStore();
  const [aliasName, setAliasName] = useState('');
  const [cashuToken, setCashuToken] = useState('');
  const [claiming, setClaiming] = useState(false);

  const proofMap = new Map<number, typeof proofs[number]>();
  proofs.forEach((p) => proofMap.set(p.addressId, p));

  const refreshAddresses = useCallback(async () => {
    try {
      const [addrData, proofsData] = await Promise.all([
        addrApi.list(),
        proofsApi.list(),
      ]);
      setAddresses(addrData.addresses || []);
      setProofs(proofsData.proofs || []);
    } catch (e) {
      console.error('Failed to load addresses:', e);
    }
  }, [setAddresses, setProofs]);

  const handleClaim = useCallback(async () => {
    const name = aliasName.trim();
    const token = cashuToken.trim();
    if (!name) {
      showToast('Enter an alias name', 'error');
      return;
    }
    if (!token) {
      showToast('Paste a Cashu token', 'error');
      return;
    }
    setClaiming(true);
    try {
      const result = await addrApi.create(name, token);
      void result;
      showToast('Alias claimed! ' + name + '@' + domain, 'success');
      setAliasName('');
      setCashuToken('');
      await refreshAddresses();
    } catch {
      showToast('Error claiming alias', 'error');
    }
    setClaiming(false);
  }, [aliasName, cashuToken, domain, showToast, refreshAddresses]);

  const handleDelete = useCallback(
    async (id: number) => {
      if (!confirm('Delete this alias? The name will be freed.')) return;
      try {
        await addrApi.delete(id);
        showToast('Alias deleted', 'success');
        await refreshAddresses();
      } catch {
        showToast('Error deleting alias', 'error');
      }
    },
    [showToast, refreshAddresses],
  );

  const handleCopy = useCallback(
    (addr: string) => {
      navigator.clipboard.writeText(addr).then(() => {
        showToast('Copied: ' + addr, 'success');
      });
    },
    [showToast],
  );

  return (
    <div class="settings-panel" id="aliases-panel" data-testid="aliases-view">
      <h2>Aliases</h2>

      <div class="settings-section">
        <h3>Your Email Addresses</h3>
        {addresses.length === 0 ? (
          <p style="color:var(--muted);font-size:13px">No addresses yet.</p>
        ) : (
          addresses.map((a) => {
            const proof = proofMap.get(a.id);
            const isTrial = proof && proof.status === 'held';
            const fullAddr = a.localPart + '@' + a.domain;
            return (
              <div
                key={a.id}
                class={'addr-card' + (isTrial ? ' trial' : '')}
                data-testid="alias-card"
              >
                <span class="addr-text">{fullAddr}</span>
                <div style="display:flex;gap:4px;align-items:center;flex-shrink:0">
                  {a.isPrimary && <span class="addr-badge primary">Primary</span>}
                  {isTrial && <span class="addr-badge trial">Trial</span>}
                  {a.isSystemAssigned && (
                    <span class="addr-badge">Auto-generated</span>
                  )}
                  <button
                    class="btn btn-ghost btn-sm"
                    style="flex-shrink:0"
                    onClick={() => handleCopy(fullAddr)}
                    data-testid="copy-address-btn"
                  >
                    Copy
                  </button>
                  {!a.isSystemAssigned && (
                    <button
                      class="btn btn-ghost btn-sm"
                      style="flex-shrink:0"
                      onClick={() => void handleDelete(a.id)}
                      data-testid="delete-alias-btn"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div class="settings-section">
        <h3>Claim Custom Alias</h3>
        <div class="pricing-grid">
          {PRICING.map((p) => (
            <div key={p.chars} class="pricing-cell">
              <div class="chars">{p.chars}</div>
              <div class="sats">{p.sats}</div>
            </div>
          ))}
        </div>
        <div class="alias-form">
          <div class="form-row">
            <input
              type="text"
              placeholder="alice"
              maxlength={63}
              value={aliasName}
              onInput={(e) => setAliasName((e.target as HTMLInputElement).value)}
              data-testid="claim-alias-input"
            />
            <button class="btn btn-sm" onClick={() => void handleClaim()} disabled={claiming} data-testid="claim-alias-btn">
              {claiming ? 'Claiming...' : 'Claim'}
            </button>
          </div>
          <textarea
            placeholder="Paste Cashu token (cashuA...)"
            value={cashuToken}
            onInput={(e) => setCashuToken((e.target as HTMLTextAreaElement).value)}
          />
          <div class="price-hint">
            {aliasName.trim() ? (
              <>
                Price: <span class="price">{priceForLength(aliasName.trim().length)} sats</span>
              </>
            ) : (
              'Enter a name to see pricing'
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
